/**
 * API contract shared by the Fastify server and React front end. Every payload shape
 * crossing the network is described here; the front end never redeclares response types.
 */
/**
 * Visual identity: the primary colour and everything derived from it. Re-exported
 * rather than left aside because the package has a single entry point, and both
 * ends import the palette from here like any other part of the contract.
 */
export * from './branding.js';
/** Thumbnail sizes the server accepts to generate. */
export const THUMB_SIZES = [320, 640, 1280];
export function isThumbSize(value) {
    return THUMB_SIZES.includes(value);
}
/**
 * Languages the interface, the emails and the server's messages are written in.
 *
 * Declared here rather than in the front end because both ends need the same list:
 * the browser announces its choice with `Accept-Language`, and the server composes
 * an email in the language the recipient reads. A locale absent from this list is
 * resolved back to `DEFAULT_LOCALE` rather than rejected — an unsupported language
 * must degrade to a readable page, never to an error.
 */
export const LOCALES = ['en', 'fr'];
/**
 * Language used when nothing else answers: an unrecognised browser preference, or a
 * recipient who has never opened the gallery. English because it is the language the
 * repository itself is written in.
 */
export const DEFAULT_LOCALE = 'en';
export function isLocale(value) {
    return typeof value === 'string' && LOCALES.includes(value);
}
/**
 * The supported language behind a language tag: `fr-CA` reads as `fr`.
 *
 * Region is dropped deliberately. The gallery translates a language, not a variant,
 * and matching `fr-FR` exactly would leave a Swiss browser in English.
 */
export function resolveLocale(tag) {
    if (!tag)
        return null;
    const base = tag.trim().toLowerCase().split('-')[0];
    return isLocale(base) ? base : null;
}
export const DEFAULT_GROUP_BY = 'month';
export function isGroupBy(value) {
    return value === 'month' || value === 'day';
}
/**
 * Direction used until something overrides it — the default for both
 * `albums.sort_order` and the API `?order=` parameter.
 *
 * `asc` because an album reads in the order it was lived: opening a trip on its last
 * day gives the ending before the beginning (D99).
 */
export const DEFAULT_SORT_ORDER = 'asc';
export function isSortOrder(value) {
    return value === 'desc' || value === 'asc';
}
/**
 * Render variant served by the media pipeline. `hd` exists for zoom: `full` is capped
 * at 2560 px, enough to fill a screen but not inspect a photo at native resolution.
 */
export const IMAGE_VARIANTS = ['full', 'hd'];
/**
 * Maximum photo-description length.
 *
 * Between the screen's other two text lengths for the same rendering reason: longer
 * than a day note (`ALBUM_DAY_DESCRIPTION_MAX_LENGTH`, 300), whose section heading
 * precalculates height without DOM (D49), and shorter than an album description
 * (`ALBUM_DESCRIPTION_MAX_LENGTH`, 2000) in a free paragraph. This sits on the photo:
 * expanded, it is capped at half the screen height, and beyond a thousand characters
 * it would stop being a caption and hide the image.
 */
export const MEDIA_DESCRIPTION_MAX_LENGTH = 1000;
/** Length of the emailed code. Six digits entered by hand. */
export const VERIFICATION_CODE_LENGTH = 6;
/* --------------------------------------------------------------------------
 * Pairing a keyboardless screen
 *
 * A television has no camera: it displays the QR code and an already signed-in phone
 * scans it. Two opposite values circulate — one intended for the whole room to see,
 * the other never to be seen. See D260809c.
 * ------------------------------------------------------------------------ */
/**
 * Displayed-code alphabet. No `I`, `O`, `0` or `1`: it is read on a screen three
 * metres away and sometimes copied by hand onto a phone.
 */
export const USER_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
/** Eight characters from `USER_CODE_ALPHABET`, providing 40 bits. */
export const USER_CODE_LENGTH = 8;
/**
 * Folds a typed or URL code into stored form: uppercase without separators. Without
 * folding, a screen code copied with its hyphen would match no request.
 */
export function normalizeUserCode(value) {
    return value.replace(/[\s-]/g, '').toUpperCase();
}
/** Readable code form, grouped by four: `ABCD-EFGH`. */
export function formatUserCode(value) {
    const code = normalizeUserCode(value);
    return code.length <= 4 ? code : `${code.slice(0, 4)}-${code.slice(4)}`;
}
/**
 * A day note is a landmark, not a story: one truncated line in a section heading whose
 * height layout precalculates. Unbounded length would require DOM measurement (D49).
 * The full text appears in the viewer banner and expands on click (D85).
 */
export const ALBUM_DAY_DESCRIPTION_MAX_LENGTH = 300;
export const ALBUM_DAY_PLACE_MAX_LENGTH = 120;
/**
 * Below this, do not search: one prefix letter would return much of the library for
 * input that says almost nothing.
 */
export const SEARCH_MIN_LENGTH = 2;
/**
 * Results returned **per type**. This is a type-ahead suggestion, not a results page:
 * beyond the limit it no longer fits and the right answer is slower to find than by
 * refining the query.
 */
export const SEARCH_HITS_PER_KIND = 5;
/**
 * Activity-feed page size.
 *
 * Large enough for the unread badge, capped at "9+", to be decided from the first
 * page alone — otherwise returning after a long absence would show "9+" then a lower
 * number while scrolling.
 */
export const COMMENTS_FEED_PAGE_SIZE = 30;
/**
 * Time during which an author may edit a comment.
 *
 * A typo-recovery window, not an editing right: long enough to reread what was sent,
 * too short to rewrite a conversation others already read (D57).
 */
export const COMMENT_EDIT_WINDOW_MS = 30_000;
/**
 * Milliseconds remaining to edit, `0` after the window closes.
 *
 * Shared because both sides must decide **identically**: the server refuses while the
 * front end stops offering. Separate calculations would diverge by a second, exactly
 * where someone clicks a button that says no.
 */
export function remainingEditMs(createdAt, now) {
    const started = Date.parse(createdAt);
    if (Number.isNaN(started))
        return 0;
    return Math.max(0, started + COMMENT_EDIT_WINDOW_MS - now);
}
/** Maximum comment length, checked identically on both sides. */
export const COMMENT_MAX_LENGTH = 2000;
/* --------------------------------------------------------------------------
 * Administration: accounts, albums and settings
 *
 * These objects are administered from the application and live in the database.
 * `config/albums.yaml` only bootstraps a new installation and is never read again once
 * the database is populated.
 * ------------------------------------------------------------------------ */
/** The `*` wildcard grants access to all present and future albums. */
export const ALL_ALBUMS = '*';
/**
 * Maximum album-description length.
 *
 * Much longer than a day note (`ALBUM_DAY_DESCRIPTION_MAX_LENGTH`) for a rendering
 * reason: the note lives in a heading whose height layout precalculates without DOM,
 * while an album description is a free paragraph with no fixed height (D49).
 */
export const ALBUM_DESCRIPTION_MAX_LENGTH = 2000;
/** Windows offered by the "Visits" tab. */
export const VISIT_WINDOWS = [7, 30, 90];
/** Default for `GET /api/admin/visits` and its accepted upper bound. */
export const VISIT_WINDOW_DEFAULT = 30;
export const VISIT_WINDOW_MAX = 365;
/** Input constraints shared for identical validation on both sides. */
export const USERNAME_PATTERN = /^[a-z0-9][a-z0-9._-]*$/i;
export const USERNAME_MAX_LENGTH = 64;
export const ALBUM_ID_PATTERN = USERNAME_PATTERN;
export const PASSWORD_MIN_LENGTH = 8;
export const DISPLAY_NAME_MAX_LENGTH = 64;
/** Maximum address length as specified by RFC 5321. */
export const EMAIL_MAX_LENGTH = 254;
/**
 * Derives an identifier from what a person typed — an album title, a storage name.
 * No accents and no spaces: the result is read from an album URL and from a
 * `connection_id` in a log line.
 *
 * Shared rather than owned by the form because the server derives a storage
 * connection's identifier with it (D260816h). A slug previewed by one
 * implementation and stored by another would eventually disagree, and the
 * identifier of a connection never changes afterwards.
 *
 * May return an empty string: a title made only of emoji or punctuation slugifies
 * to nothing, and whoever calls this decides what to do about it — a form asks
 * again, the storage route falls back.
 */
export function slugifyAlbumId(title) {
    const slug = title
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    return slug.slice(0, USERNAME_MAX_LENGTH);
}
//# sourceMappingURL=index.js.map